<template>
  <div
    v-if="showEndSoonTag || showNextBatch"
    class="purchase__market d-flex align-center mb-0-2"
  >
    <icon v-if="showEndSoonTag" name="estimated-time" class="mr-0-2" />

    <count-down
      :before-text="$t('purchase.timer.only')"
      :end="
        shopifyProduct.isSaleableAsStock
          ? shopifyProduct.endDate
          : showEndSoonTag
          ? productDocument.dates.end
          : productDocument.current_batch_date_end
      "
      :over-text="$t('purchase.timer.over')"
      :skeleton="!isRefreshed"
      class="text-big w-600"
      show-unit
    ></count-down>

    <icon v-if="!showEndSoonTag" name="info" class="ml-0-2" />
  </div>
</template>

<script>
import { differenceInDays, isFuture, isPast } from 'date-fns'

import Icon from '@/components/icons/Icon'
import DeliveryMixin from '~/mixins/deliveryText'

export default {
  name: 'PurchaseTimer',
  components: {
    Icon,
    CountDown: () => ({
      component: import(
        '@/components/countdown/CountDown' /* webpackChunkName: 'countdown' */
      ),
    }),
  },
  mixins: [DeliveryMixin],
  props: {
    productDocument: {
      type: Object,
      required: true,
    },
    shopifyProduct: {
      type: Object,
      required: true,
    },
    isRefreshed: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    showEndSoonTag() {
      if (
        this.shopifyProduct.hasDates &&
        this.shopifyProduct.isSaleableAsStock
      ) {
        return this.shouldShowTimer(this.shopifyProduct.endDate)
      }

      if (this.productDocument.dates.end) {
        return this.shouldShowTimer(this.productDocument.dates.end)
      }

      return false
    },

    showNextBatch() {
      if (
        this.productDocument.current_batch_date_end &&
        this.productDocument.next_delivery_date
      ) {
        return (
          isFuture(this.productDocument.current_batch_date_end) &&
          differenceInDays(
            this.productDocument.current_batch_date_end,
            new Date()
          ) <= 14
        )
      }
      return false
    },
    delivery() {
      return this.shopifyProduct.metafield
        ? new Date(this.shopifyProduct.metafield)
        : undefined
    },
    nextBatchDeliveryOptions() {
      if (
        this.delivery !== undefined &&
        this.productDocument.next_delivery_date
      ) {
        return {
          ...this.getDeliveryFromDate(this.delivery, 'current').options,
          ...this.getDeliveryFromDate(
            this.productDocument.next_delivery_date,
            'next'
          ).options,
        }
      }

      return {}
    },
  },
  mounted() {
    if (this.showNextBatch && !this.showEndSoonTag) {
      this.initTootip()
    }
  },
  methods: {
    shouldShowTimer(date) {
      const now = new Date()
      return isPast(date) || differenceInDays(date, now) <= 10
    },
    async initTootip() {
      try {
        const tippy = await import('tippy.js' /* webpackChunkName: 'tippy' */)
        await import(
          '@/scss/09-libs/_06-tippy.scss' /* webpackChunkName: 'tippy_css' */
        )
        await import(
          'tippy.js/animations/scale.css' /* webpackChunkName: 'tippy_animation_scale_css' */
        )

        tippy.default(this.$el, {
          theme: 'dark',
          placement: 'top',
          animation: 'scale',
          arrow: true,
          onShow: (instance) => {
            if (this.isRefreshed) {
              return instance.setContent(
                this.$t(
                  'purchase.precoEndTooltip',
                  this.nextBatchDeliveryOptions
                )
              )
            }

            instance._wait = setInterval((_) => {
              if (this.isRefreshed) {
                instance.setContent(
                  this.$t(
                    'purchase.precoEndTooltip',
                    this.nextBatchDeliveryOptions
                  )
                )
                clearInterval(instance._wait)
              } else {
                instance.setContent(this.$t('loading'))
              }
            }, 500)
          },
          onHidden(instance) {
            clearInterval(instance._wait)
          },
        })
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error while loading tippy.js', e)
      }
    },
  },
}
</script>

<style lang="scss">
.purchase__market {
  font-family: var(--tertiary-font-family);
  font-size: var(--extra-small-font-size);
  line-height: 1;
  background-color: var(--yellow-color);
  padding: rem(5px) calc(var(--spacing) * 0.6);
  gap: calc(var(--spacing) * 0.2);
  transition: all 0.3s var(--asphalte-animation-function);

  .countdown {
    font-size: var(--extra-small-font-size);
  }

  .countdown__before {
    padding-left: 0;
    padding-right: calc(var(--spacing) * 0.4);
  }

  .reduced & {
    opacity: 0;
  }
}
</style>
